import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { Link } from "react-router-dom";

const IMG = {
  djs: "/images/dj2.webp",
  wedding: "/images/dj8.webp",
  crowd: "/images/dj3.webp",
};

const chapters = [
  {
    num: "01",
    title: "The Vibe",
    text: "Every crowd has a heartbeat. Our job is to find it in the first three songs, reading the room, blending Bollywood with Top 40, classics with what's trending, until grandma and your college friends are on the same dance floor.",
  },
  {
    num: "02",
    title: "The Elegance",
    text: "Premium sound, intelligent lighting, and a presence that elevates the room without stealing the spotlight. We dress the part, arrive early, and treat your venue, ballroom or backyard, like a stage worthy of your story.",
  },
  {
    num: "03",
    title: "The Energy",
    text: "A packed dance floor isn't luck. It's timing, instinct, and ten years of knowing exactly when to drop that song. We don't take breaks, we don't lose the room, and we don't stop until the lights come on.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHeader
        center
        overline="Our Story"
        title="The dream behind"
        accent="the decks."
        description="Dream Entertainment was born from one belief: the right music, at the right moment, turns a gathering into a memory people talk about for decades."
      />

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative">
            <Reveal>
              <div className="rounded-2xl overflow-hidden border border-white/5 h-[420px] sm:h-[520px]">
                <img
                  src={IMG.djs}
                  alt="Dream Entertainment DJs mixing live"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={0.2} className="hidden sm:block absolute -bottom-12 -right-4 lg:-right-12 w-64">
              <div className="rounded-2xl overflow-hidden border-4 border-background shadow-2xl h-72">
                <img
                  src={IMG.wedding}
                  alt="Dream Entertainment DJ at a white booth"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5 lg:pl-8 sm:pt-8">
            <Overline>Since Day One</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mt-6">
              One turntable. One dream.{" "}
              <em className="text-primary">Hundreds of unforgettable nights.</em>
            </h2>
            <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed">
              What started as a passion for music became a full-scale
              entertainment company trusted for life&rsquo;s biggest moments.
              From intimate backyard baby showers to grand Indian wedding
              receptions, we&rsquo;ve learned one thing: no two celebrations
              are alike, and none should sound alike either.
            </p>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              That&rsquo;s why every event begins with a conversation, not a
              contract. Your story shapes the soundtrack.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32">
        <Reveal>
          <Overline>The Manifesto</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 mb-20">
            Three chapters. <em className="text-primary">One promise.</em>
          </h2>
        </Reveal>
        <div className="space-y-24">
          {chapters.map((c, i) => (
            <Reveal key={c.num} delay={0.05}>
              <div
                data-testid={`manifesto-chapter-${c.num}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start ${
                  i % 2 === 1 ? "lg:text-right" : ""
                }`}
              >
                <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <span className="font-serif text-[8rem] sm:text-[10rem] leading-none text-outline select-none">
                    {c.num}
                  </span>
                </div>
                <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-1" : ""} lg:pt-10`}>
                  <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl lg:inline-block">
                    {c.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FULL WIDTH IMAGE + CTA */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src={IMG.crowd}
          alt="Dream Entertainment DJ performing live"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
          <Reveal className="text-center px-6">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Let&rsquo;s write <em className="text-primary">your chapter.</em>
            </h2>
            <Link
              to="/booking"
              data-testid="about-cta-btn"
              className="mt-10 inline-block rounded-full bg-primary text-primary-foreground px-10 py-4 text-sm font-medium tracking-widest uppercase transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,0,127,0.4)]"
            >
              Book Your Date
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
